import { enviarFormulario, obterJson } from './v2/api';
import { avisarErro } from './v2/aviso';
import { comBotao, ocupado, ocupar } from './v2/botao';
import { aoExibirPagina, config, formatar, textos, url } from './v2/config';
import { iniciarMedicaoPagina } from './v2/medicao';

// Central de Ajuda do candidato (Central-de-Ajuda.md §3.3). As páginas são iguais para todos e ficam na borda: o voto pede o
// token antiforgery na hora, e a visualização do artigo e o clique na busca vão por beacon, sem parâmetro na URL do artigo.
const computador = window.matchMedia('(min-width: 960px)');
const semAnimacao = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
// Ctrl/Cmd/Shift ou botão do meio abre em outra aba: não interceptar (o mesmo guarda do topo).
const cliqueComum = e => e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey;
let _campos = null;
// O chunk que não baixou não fica guardado: o próximo envio tenta de novo.
const campos = () => (_campos ??= import(/* webpackChunkName: "campos" */ './v2/campos').catch(e => {
    _campos = null;
    throw e;
}));

// A página não traz o token (ficaria fora da borda): pede um na 1ª vez; o 400 de token velho renova pelo mesmo endereço.
async function garantirToken() {
    const c = config();
    c.afNovo = url('/ajuda/token');
    if (!c.af) {
        const r = await obterJson(c.afNovo, { semCache: true }).catch(() => null);
        if (r?.af) {
            c.af = r.af;
        }
    }
}

// A medição nunca atrapalha a página: sem o sendBeacon, um fetch que sobrevive à troca de página.
function _beacon(caminho, valores) {
    const dados = new FormData();
    Object.entries(valores).forEach(([chave, valor]) => dados.append(chave, String(valor)));
    try {
        if (!navigator.sendBeacon?.(url(caminho), dados)) {
            fetch(url(caminho), { method: 'POST', body: dados, credentials: 'same-origin', keepalive: true }).catch(() => { });
        }
    } catch {
        // Sem medição desta vez.
    }
}

// ---------- busca: o campo vazio mostra o erro embaixo dele ----------
document.querySelectorAll('[data-aj-busca]').forEach(form => {
    const campo = form.querySelector('input[name="q"]');
    const regra = () => (campo.value.trim().length >= 2 ? '' : form.dataset.erro);
    let aoVivo = false;
    form.addEventListener('submit', async e => {
        if (!regra()) {
            // A página da busca espera a resposta da assistente: a roda mostra que o pedido saiu e o 2º clique não envia de novo
            // (dono, 04/11/2026). Na volta pelo cache do navegador, o botao.js a tira.
            const botao = form.querySelector('[type="submit"]');
            if (ocupado(botao)) {
                e.preventDefault();
                return;
            }
            ocupar(botao);
            return;
        }
        e.preventDefault();
        const { mostrarErros, validarAoVivo } = await campos();
        mostrarErros([[campo, regra()]]);
        if (!aoVivo) {
            aoVivo = true;
            validarAoVivo(form, { [campo.id]: regra });
        }
    });
});

// ---------- sumário: o do topo (celular, em <details>) e o da lateral (computador, preso); a seção na tela marcada pelo
// IntersectionObserver nos dois ----------
const sumarios = [...document.querySelectorAll('.aj-toc')];
const detalhes = document.querySelector('.aj-toc--topo details');
const links = sumarios.flatMap(s => [...s.querySelectorAll('a[href^="#"]')]);
// Um id com "%" solto faria o decodeURIComponent lançar e parar o script inteiro.
const idDo = link => {
    try {
        return decodeURIComponent(link.hash.slice(1));
    } catch {
        return link.hash.slice(1);
    }
};
const titulos = [...new Set(links.map(a => document.getElementById(idDo(a))).filter(Boolean))];
let _segurar = null;

function _marcar(id) {
    links.forEach(a => {
        if (idDo(a) === id) {
            a.setAttribute('aria-current', 'location');
        } else {
            a.removeAttribute('aria-current');
        }
    });
}

// A seção atual é o último título que já passou do topo preso (o clique no sumário vale até a rolagem chegar).
function _secaoAtual() {
    if (_segurar && Date.now() < _segurar.ate) {
        return _segurar.id;
    }
    const limite = (document.querySelector('.site-h')?.getBoundingClientRect().bottom || 0) + 32;
    let atual = titulos[0]?.id;
    titulos.forEach(t => {
        if (t.getBoundingClientRect().top <= limite) {
            atual = t.id;
        }
    });
    return atual;
}

if (titulos.length) {
    // Só quando um título cruza a faixa de cima da tela (sem ouvir a rolagem inteira).
    const observador = new IntersectionObserver(() => _marcar(_secaoAtual()), { rootMargin: '-72px 0px -55% 0px', threshold: [0, 1] });
    titulos.forEach(t => observador.observe(t));
}
sumarios.forEach(sumario => sumario.addEventListener('click', e => {
    const link = e.target.closest('a[href^="#"]');
    const titulo = link && document.getElementById(idDo(link));
    if (!titulo || !cliqueComum(e)) {
        return;
    }
    e.preventDefault();
    _segurar = { id: titulo.id, ate: Date.now() + 800 };
    _marcar(titulo.id);
    if (!computador.matches && detalhes) {
        detalhes.open = false;
    }
    // O scroll-margin-top do título desconta o topo preso; o endereço ganha o #, para o link poder ser copiado.
    titulo.scrollIntoView({ behavior: semAnimacao() ? 'auto' : 'smooth', block: 'start' });
    history.replaceState(history.state, '', link.hash);
    titulo.focus({ preventScroll: true });
}));
if (titulos.length) {
    _marcar(_secaoAtual());
}

// ---------- "Este artigo ajudou?" ----------
const fb = document.querySelector('[data-aj-fb]');
if (fb) {
    const T = textos(fb);
    const pergunta = fb.querySelector('[data-fb-pergunta]');
    const form = fb.querySelector('[data-fb-form]');
    const fim = fb.querySelector('[data-fb-fim]');
    const motivos = [...form.querySelectorAll('input[name="motivo"]')];
    const erroEnvio = document.getElementById('ajFbErro');
    const regraMotivo = () => (motivos.some(m => m.checked) ? '' : T.erroMotivo);

    const enviar = async valores => {
        await garantirToken();
        const dados = new FormData();
        dados.append('artigo', fb.dataset.ajFb);
        Object.entries(valores).forEach(([chave, valor]) => dados.append(chave, String(valor)));
        return enviarFormulario(url('/ajuda/feedback'), dados).catch(() => null);
    };
    const terminar = util => {
        pergunta.hidden = true;
        form.hidden = true;
        fim.querySelector('[data-fb-mais]').hidden = util;
        fim.hidden = false;
        fim.focus();
    };

    // O voto já grava no toque (o "Não" sem motivo também conta); o motivo e o texto chegam depois, no mesmo voto.
    pergunta.addEventListener('click', async e => {
        const botao = e.target.closest('[data-voto]');
        if (!botao) {
            return;
        }
        const util = botao.dataset.voto === '1';
        const resposta = await comBotao(botao, () => enviar({ util }));
        if (resposta === undefined) {
            return;
        }
        if (!resposta?.sucesso) {
            avisarErro(resposta?.mensagem || T.erro);
            return;
        }
        pergunta.querySelectorAll('[data-voto]').forEach(b => b.setAttribute('aria-pressed', String(b === botao)));
        if (util) {
            terminar(true);
            return;
        }
        form.hidden = false;
        motivos[0]?.focus();
    });

    form.addEventListener('change', async e => {
        if (e.target.name === 'motivo' && motivos[0].getAttribute('aria-invalid') === 'true') {
            (await campos()).mostrarErro(motivos[0], '');
        }
    });

    form.addEventListener('submit', async e => {
        e.preventDefault();
        const { mostrarErro, mostrarErros } = await campos();
        mostrarErro(null, '', erroEnvio);
        if (mostrarErros([[motivos[0], regraMotivo()]])) {
            return;
        }
        const resposta = await comBotao(form.querySelector('[type="submit"]'), () => enviar({
            util: false,
            motivo: motivos.find(m => m.checked).value,
            texto: form.querySelector('textarea').value.trim()
        }));
        if (resposta === undefined) {
            return;
        }
        if (!resposta?.sucesso) {
            mostrarErro(null, resposta?.mensagem || T.erro, erroEnvio);
            return;
        }
        terminar(false);
    });

    // Voto errado: a pergunta volta limpa, e o próximo voto troca o anterior no servidor.
    fim.querySelector('[data-fb-mudar]')?.addEventListener('click', async () => {
        fim.hidden = true;
        form.hidden = true;
        form.reset();
        // O reset não tira o erro de um envio sem motivo de antes: o "Não" seguinte reabriria o form já com ele.
        const { mostrarErro } = await campos();
        mostrarErro(motivos[0], '');
        mostrarErro(null, '', erroEnvio);
        pergunta.querySelectorAll('[data-voto]').forEach(b => b.setAttribute('aria-pressed', 'false'));
        pergunta.hidden = false;
        pergunta.querySelector('[data-voto]').focus();
    });
}

// ---------- indicadores ----------
const artigo = document.querySelector('[data-aj-artigo]');
if (artigo) {
    aoExibirPagina(() => _beacon('/ajuda/visualizacao', { artigo: artigo.dataset.ajArtigo }));
}

const resultados = document.querySelector('[data-aj-interacao]');
if (resultados) {
    const aoAbrir = e => {
        const link = e.target.closest('a[data-aj-id]');
        if (link && (e.type === 'click' || e.button === 1)) {
            _beacon('/ajuda/clique', { interacao: resultados.dataset.ajInteracao, artigo: link.dataset.ajId });
        }
    };
    resultados.addEventListener('click', aoAbrir);
    resultados.addEventListener('auxclick', aoAbrir);
}

// ---------- "Perguntar à assistente": o campo da busca (na mesma página, ou no início pelo #ajQ do artigo) recebe o foco ----------
function _focarPergunta() {
    const campo = document.getElementById('ajQ');
    if (!campo) {
        return;
    }
    campo.scrollIntoView({ behavior: semAnimacao() ? 'auto' : 'smooth', block: 'center' });
    campo.focus({ preventScroll: true });
}
document.querySelectorAll('[data-aj-perguntar]').forEach(link => link.addEventListener('click', e => {
    if (cliqueComum(e) && document.getElementById('ajQ')) {
        e.preventDefault();
        _focarPergunta();
    }
}));
if (location.hash === '#ajQ') {
    _focarPergunta();
}

// ---------- a assistente (F5b): pedida depois da página; a resposta vem em blocos já conferidos pelo servidor (AjudaRespostaFormatada:
// segmentos de texto, negrito e links com o endereço conferido) e entra por createElement/textContent, nada de HTML vindo da IA ----------
const CHAVE_PERGUNTA = 'ajuda:pergunta';

function _el(tag, classe, texto) {
    const e = document.createElement(tag);
    if (classe) {
        e.className = classe;
    }
    if (texto !== undefined && texto !== null) {
        e.textContent = texto;
    }
    return e;
}

function _icone(nome) {
    const ns = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('class', 'ico');
    svg.setAttribute('aria-hidden', 'true');
    const use = document.createElementNS(ns, 'use');
    use.setAttribute('href', `#i-${nome}`);
    svg.append(use);
    return svg;
}

// A pergunta e o assunto da IA vão ao Contato pelo sessionStorage, nunca pela URL (fica em log e na borda), com a origem: o Contato
// só os usa quando chega pelo mesmo link.
function _linkChamado(origem, rotulo, pergunta, chamado) {
    const link = _el('a', 'btn btn--navy btn--sm');
    link.href = `${url('/suporte/novo')}#origem=${origem}`;
    link.append(_icone('headset'), document.createTextNode(rotulo));
    if (pergunta) {
        // Só no clique comum: o Ctrl/Cmd+clique abre outra aba, e a chave ficaria nesta para um chamado depois.
        link.addEventListener('click', e => {
            if (!cliqueComum(e)) {
                return;
            }
            try {
                sessionStorage.setItem(CHAVE_PERGUNTA, JSON.stringify({ origem, pergunta, chamado: typeof chamado === 'string' ? chamado : null }));
            } catch {
                // Sem o sessionStorage, o Contato abre com a descrição vazia.
            }
        });
    }
    return link;
}

// Só endereço deste site: o servidor monta a URL da fonte, e isto é a 2ª trava. Caractere de controle sai antes ("/\t/x" vira "//x").
function _hrefDaFonte(u) {
    const proibido = c => c.charCodeAt(0) < 32 || c.charCodeAt(0) === 127 || c === '\\';
    if (typeof u !== 'string' || !u.startsWith('/') || u.startsWith('//') || [...u].some(proibido)) {
        return null;
    }
    try {
        return new URL(u, location.origin).origin === location.origin ? u : null;
    } catch {
        return null;
    }
}

// O link de tela que o servidor conferiu nos trechos: do próprio site vem relativo; de fora, só http(s), e abre em outra aba.
function _hrefDaTela(u) {
    const local = _hrefDaFonte(u);
    if (local) {
        return { href: local, fora: false };
    }
    if (typeof u !== 'string' || [...u].some(c => c.charCodeAt(0) <= 32 || c.charCodeAt(0) === 127 || c === '\\')) {
        return null;
    }
    try {
        const destino = new URL(u);
        return /^https?:$/.test(destino.protocol) ? { href: destino.href, fora: destino.origin !== location.origin } : null;
    } catch {
        return null;
    }
}

function _linkDaFonte(classe, fonte) {
    const href = _hrefDaFonte(fonte.url);
    const e = _el(href ? 'a' : 'span', classe);
    if (href) {
        e.href = href;
        e.dataset.ajId = fonte.artigo;
    }
    return e;
}

// Os segmentos de um texto: o negrito (nome de botão ou menu) em <strong>; o link só com o endereço conferido (artigo deste site ou
// tela que estava escrita num trecho). Sem endereço válido, fica o texto.
function _segmentos(destino, segmentos, T) {
    (Array.isArray(segmentos) ? segmentos : []).forEach(s => {
        if (!s || typeof s.texto !== 'string' || !s.texto) {
            return;
        }
        const conteudo = s.negrito ? _el('strong', null, s.texto) : document.createTextNode(s.texto);
        const href = s.artigo ? _hrefDaFonte(s.url) : null;
        const alvo = href ? { href, fora: false } : (!s.artigo && s.url ? _hrefDaTela(s.url) : null);
        if (!alvo) {
            destino.append(conteudo);
            return;
        }
        const a = _el('a', 'aj-ia__a');
        a.href = alvo.href;
        a.append(conteudo);
        if (s.artigo) {
            a.dataset.ajId = s.artigo;
        }
        if (alvo.fora) {
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            a.append(_el('span', 'sr', ` ${T.novaAba}`));
        }
        destino.append(a);
    });
    return destino;
}

// Um bloco da resposta: passos numerados (o círculo lavanda dos artigos), lista, dica, atenção ou parágrafo.
function _bloco(b, T) {
    if (!b) {
        return null;
    }
    if (b.tipo === 'passos' || b.tipo === 'lista') {
        const itens = (b.itens || []).filter(i => Array.isArray(i) && i.length);
        if (!itens.length) {
            return null;
        }
        const caixa = _el('div', 'aj-ia__grupo');
        if (b.titulo) {
            caixa.append(_el('p', 'aj-ia__bt', b.titulo));
        }
        const passos = b.tipo === 'passos';
        const lista = _el(passos ? 'ol' : 'ul', passos ? 'aj-ia__passos' : 'aj-ia__lista');
        // Sem marcador visível, o Safari tira a semântica de lista: o role devolve.
        lista.setAttribute('role', 'list');
        itens.forEach(item => lista.append(_segmentos(_el('li'), item, T)));
        caixa.append(lista);
        return caixa;
    }
    if (!Array.isArray(b.texto) || !b.texto.length) {
        return null;
    }
    if (b.tipo === 'dica' || b.tipo === 'atencao') {
        const dica = b.tipo === 'dica';
        const nota = _el('div', `aj-ia__nota aj-ia__nota--${b.tipo}`);
        const icone = _el('span', 'aj-ia__nota-ic');
        icone.append(_icone(dica ? 'lightbulb' : 'exclamation-triangle'));
        const corpo = _el('div', 'aj-ia__nota-c');
        corpo.append(_el('b', 'aj-ia__nota-t', dica ? T.dica : T.atencao), _segmentos(_el('p'), b.texto, T));
        nota.append(icone, corpo);
        return nota;
    }
    return _segmentos(_el('p', 'aj-ia__txt'), b.texto, T);
}

// "Perguntas relacionadas": a busca roda de novo com a sugestão e o artigo que a responde (?artigo=), que a assistente usa primeiro.
const ARTIGO = /^[1-9]\d{0,8}$/;
function _perguntarDeNovo(texto, artigo) {
    const fixo = ARTIGO.test(String(artigo)) ? `&artigo=${artigo}` : '';
    location.assign(`${url('/ajuda/busca')}?q=${encodeURIComponent(texto)}${fixo}`);
}

// O artigo da pergunta relacionada que a página recebeu (data-artigo); nulo fora dela.
function _artigoDaRelacionada(caixa) {
    try {
        const a = JSON.parse(caixa.dataset.artigo || 'null');
        return a && ARTIGO.test(String(a.artigo)) && typeof a.titulo === 'string' ? a : null;
    } catch {
        return null;
    }
}

function _respostaDaAssistente(caixa, r, T, pergunta) {
    // A relacionada nunca termina em "não encontrei": falha (até a de rede), limite ou sem resposta mostram o artigo dela.
    const daRelacionada = _artigoDaRelacionada(caixa);
    const respondeu = r?.estado === 'resposta' && (r.resumo?.length || r.blocos?.length);
    if (daRelacionada && r && !respondeu && r.estado !== 'artigo' && r.estado !== 'desligada') {
        r = { estado: 'artigo', fontes: [daRelacionada], interacao: r.interacao, chamado: r.chamado };
    }
    const corpo = caixa.querySelector('[data-ia-corpo]');
    const status = caixa.querySelector('[data-ia-status]');
    const origem = r?.interacao ? `interacao-${r.interacao}` : caixa.dataset.origem || 'ajuda';
    const vazio = document.querySelector('[data-aj-vazio]');
    const conteudo = [];
    const soArtigo = r?.estado === 'artigo' && r.fontes?.length > 0;
    let aviso;
    let focarDepois = null;

    if (!r || r.estado === 'desligada') {
        caixa.hidden = true;
        return;
    }
    if (r.estado === 'limite') {
        caixa.classList.add('aj-ia--limite');
        aviso = formatar(vazio ? T.limiteSemLista : T.limite, r.limite);
        conteudo.push(_el('p', 'aj-ia__txt', aviso));
    } else if (!soArtigo && (r.estado !== 'resposta' || (!r.resumo?.length && !r.blocos?.length))) {
        // Sem resposta (nenhum trecho, ou a IA não achou) e falha: o mesmo texto, com o chamado (que já leva a pergunta).
        aviso = T.semResposta;
        conteudo.push(_el('p', 'aj-ia__txt', aviso));
        const bts = _el('div', 'aj-ia__bts');
        const chamado = _linkChamado(origem, T.abrirChamado, pergunta, r.chamado);
        bts.append(chamado);
        conteudo.push(bts);
        // A lista vazia diria o mesmo logo abaixo; o foco que estava nela vai para o chamado do cartão.
        if (vazio) {
            if (vazio.contains(document.activeElement)) {
                focarDepois = chamado;
            }
            vazio.hidden = true;
        }
    } else {
        aviso = soArtigo ? T.noArtigo : T.pronta;
        // Com resposta (ou o artigo da relacionada), a lista vazia abaixo diria "não encontrei": sai, e o foco que estava nela vai para o cartão.
        let focarNoArtigo = false;
        if (vazio) {
            focarNoArtigo = vazio.contains(document.activeElement);
            vazio.hidden = true;
        }
        if (soArtigo) {
            conteudo.push(_el('p', 'aj-ia__txt', T.noArtigo));
        }
        // O resumo abre a resposta (a frase que já responde); os blocos vêm depois, na ordem da IA.
        if (r.resumo?.length) {
            conteudo.push(_segmentos(_el('p', 'aj-ia__resumo'), r.resumo, T));
        }
        (r.blocos || []).forEach(b => {
            const no = _bloco(b, T);
            if (no) {
                conteudo.push(no);
            }
        });

        // A IA disse que só o suporte resolve: o chamado com a pergunta já aparece, antes do "Isso resolveu?".
        let chamadoAntes = null;
        if (r.precisaChamado) {
            const bts = _el('div', 'aj-ia__bts');
            chamadoAntes = _linkChamado(origem, T.chamadoComPergunta, pergunta, r.chamado);
            bts.append(chamadoAntes);
            conteudo.push(bts);
        }

        // "Saiba mais nos artigos": um cartão por artigo usado, no lugar das citações numeradas no texto.
        if (r.fontes?.length) {
            const mais = _el('div', 'aj-ia__mais');
            const lista = _el('ul', 'aj-ia__fontes');
            lista.setAttribute('role', 'list');
            r.fontes.forEach(f => {
                const li = _el('li');
                const cartao = _linkDaFonte('aj-ia__fonte', f);
                const icone = _el('span', 'aj-ia__fonte-ic');
                icone.append(_icone('journal-text'));
                const texto = _el('span', 'aj-ia__fonte-c');
                texto.append(_el('span', 'aj-ia__ft', f.secao ? `${f.titulo}: ${f.secao}` : f.titulo), _el('small', null, f.tema));
                cartao.append(icone, texto);
                if (cartao.tagName === 'A') {
                    cartao.append(_icone('arrow-right'));
                }
                li.append(cartao);
                lista.append(li);
            });
            if (soArtigo) {
                mais.append(lista);
            } else {
                mais.append(_el('h3', 'aj-ia__sub', T.fontes), lista);
            }
            if (focarNoArtigo) {
                focarDepois = lista.querySelector('a');
            }
            conteudo.push(mais);
        }

        // Sem a pergunta gravada (falha da IA ou cota) não há "Isso resolveu?": o chamado com a pergunta fica à mão.
        if (soArtigo && !r.interacao) {
            const bts = _el('div', 'aj-ia__bts');
            bts.append(_linkChamado(origem, T.chamadoComPergunta, pergunta, r.chamado));
            conteudo.push(bts);
        }

        // "Perguntas relacionadas": botões que refazem a pergunta com o artigo que a responde.
        const sugestoes = (r.sugestoes || []).filter(s => typeof s?.texto === 'string' && s.texto.trim());
        if (sugestoes.length) {
            const relacionadas = _el('div', 'aj-ia__sug');
            const chips = _el('div', 'aj-ia__chips');
            sugestoes.forEach(s => {
                const chip = _el('button', 'chip aj-ia__chip');
                chip.type = 'button';
                chip.append(_icone('search'), document.createTextNode(s.texto));
                chip.addEventListener('click', () => _perguntarDeNovo(s.texto, s.artigo));
                chips.append(chip);
            });
            relacionadas.append(_el('h3', 'aj-ia__sub', T.sugestoes), chips);
            conteudo.push(relacionadas);
        }

        // "Isso resolveu?": o Sim e o Não gravam na pergunta; o Não leva ao chamado com a pergunta escrita. Sem a pergunta gravada (a
        // gravação falhou ou bateu na cota), não há onde marcar.
        if (r.interacao) {
            const fbIa = _el('div', 'aj-ia__fb');
            fbIa.setAttribute('role', 'group');
            fbIa.setAttribute('aria-labelledby', 'ajIaResolveu');
            const rotulo = _el('b', null, T.resolveu);
            rotulo.id = 'ajIaResolveu';
            const sim = _el('button', 'btn btn--out btn--sm');
            sim.type = 'button';
            sim.append(_icone('check-lg'), document.createTextNode(T.sim));
            const nao = _el('button', 'btn btn--out btn--sm');
            nao.type = 'button';
            nao.append(_icone('x-lg'), document.createTextNode(T.nao));
            fbIa.append(rotulo, sim, nao);
            const responder = async (botao, resolveu) => {
                // Um voto por vez: o Não clicado enquanto o Sim ainda vai mandaria o 2º POST.
                if (ocupado(sim) || ocupado(nao)) {
                    return;
                }
                const outro = botao === sim ? nao : sim;
                const dados = new FormData();
                dados.append('interacao', r.interacao);
                dados.append('resolveu', resolveu);
                outro.setAttribute('aria-disabled', 'true');
                const resposta = await comBotao(botao, async () => {
                    await garantirToken();
                    return enviarFormulario(url('/ajuda/resolveu'), dados, { silencioso: true }).catch(() => null);
                });
                outro.removeAttribute('aria-disabled');
                if (resposta === undefined) {
                    return;
                }
                if (!resposta?.sucesso) {
                    avisarErro(T.erro);
                    return;
                }
                const fim = _el('div', `aj-ia__fb aj-ia__fb--fim${resolveu ? ' aj-ia__fb--ok' : ''}`);
                fim.tabIndex = -1;
                if (resolveu) {
                    fim.append(_icone('check-circle-fill'), _el('b', null, T.queBom));
                } else {
                    fim.append(_el('b', null, T.quePena));
                    // Com o chamado já na tela (precisaChamado), não repete o botão.
                    if (!r.precisaChamado) {
                        const bts = _el('div', 'aj-ia__bts');
                        bts.append(_linkChamado(origem, T.chamadoComPergunta, pergunta, r.chamado));
                        fim.append(bts);
                    }
                }
                fbIa.replaceWith(fim);
                // Com o chamado já acima (precisaChamado), o foco vai para ele: o "Abra um chamado:" aponta para lá.
                (!resolveu && chamadoAntes ? chamadoAntes : fim).focus();
            };
            sim.addEventListener('click', () => responder(sim, true));
            nao.addEventListener('click', () => responder(nao, false));
            conteudo.push(fbIa);
        }
    }

    const nos = conteudo.filter(n => !(n.tagName === 'P' && !n.childNodes.length));
    // A resposta entra em etapas, bloco a bloco e rápido (o CSS desliga com movimento reduzido).
    nos.forEach((n, i) => n.style.setProperty('--i', String(i)));
    corpo.classList.add('aj-ia__corpo--entra');
    corpo.replaceChildren(...nos);
    focarDepois?.focus();
    // O corpo inteiro não é região viva (leria a resposta toda a cada troca): só o aviso curto.
    if (status) {
        status.textContent = aviso || '';
    }
    // O clique numa citação ou numa fonte marca o artigo aberto na pergunta (como o clique na lista).
    if (r.interacao) {
        const aoAbrir = e => {
            const link = e.target.closest('a[data-aj-id]');
            if (link && (e.type === 'click' || e.button === 1)) {
                _beacon('/ajuda/clique', { interacao: r.interacao, artigo: link.dataset.ajId });
            }
        };
        corpo.addEventListener('click', aoAbrir);
        corpo.addEventListener('auxclick', aoAbrir);
    }
}

async function _iniciarAssistente(caixa) {
    const T = textos(caixa);
    const pergunta = caixa.dataset.pergunta || '';
    const daPagina = document.querySelector('[data-aj-interacao]')?.dataset.ajInteracao;
    await garantirToken();
    const dados = new FormData();
    dados.append('q', pergunta);
    if (daPagina) {
        dados.append('interacao', daPagina);
    }
    const artigo = new URLSearchParams(location.search).get('artigo');
    if (ARTIGO.test(artigo || '')) {
        dados.append('artigo', artigo);
    }
    // O servidor desiste da IA em 25 s; aqui, com folga para a resposta chegar.
    const r = await enviarFormulario(url('/ajuda/responder'), dados, { silencioso: true, tempo: 32000 }).catch(() => ({ estado: 'falha' }));
    caixa.removeAttribute('aria-busy');
    _respostaDaAssistente(caixa, r, T, pergunta);
}

const assistente = document.querySelector('[data-aj-ia]');
if (assistente) {
    aoExibirPagina(() => _iniciarAssistente(assistente));
}

iniciarMedicaoPagina('ajuda');
